import { toast } from "sonner";
import { isServiceUpdating, SERVICE_UPDATING_MESSAGE, SERVICE_UPDATING_TOAST_ID } from './service-status';
import {
  useSyncStore,
  SYNC_WARNING_MS,
  SYNC_ERROR_MS,
  type SyncAction,
} from "@/stores/sync.store";

const originalDismiss = toast.dismiss;
const originalWarning = toast.warning;
const originalError = toast.error;

let silentSeq = 0;
function nextSilentId(prefix: string, explicitId?: unknown): string | number {
  if (typeof explicitId === "string" || typeof explicitId === "number") {
    return explicitId;
  }
  silentSeq += 1;
  return `silent-${prefix}-${silentSeq}`;
}

function isSilentToastId(id?: string | number): boolean {
  return (
    typeof id === "string" &&
    (id.startsWith("dummy-") || id.startsWith("silent-") || id.startsWith("dedup-"))
  );
}

function extractSyncAction(data?: unknown): SyncAction | null {
  if (!data || typeof data !== "object") {
    return null;
  }
  const candidate = (data as { action?: unknown }).action;
  if (!candidate || typeof candidate !== "object") {
    return null;
  }
  const label = (candidate as { label?: unknown }).label;
  const onClick = (candidate as { onClick?: unknown }).onClick;
  if (typeof label === "string" && typeof onClick === "function") {
    return {
      label,
      onClick: () => {
        (onClick as (event?: unknown) => void)(undefined);
      },
    };
  }
  return null;
}

// Helper to find and focus target input element based on error message text
function focusFieldByErrorMessage(message: string) {
  if (typeof document === "undefined") {
    return;
  }
  const msg = message.toLowerCase();
  let fieldName = "";

  if (
    msg.includes("mã khách hàng") ||
    msg.includes("customer code") ||
    (msg.includes("mã") &&
      msg.includes("đã được sử dụng") &&
      document.querySelector('[name="code"]'))
  ) {
    fieldName = "code";
  } else if (
    msg.includes("mã sản phẩm") ||
    msg.includes("product code") ||
    msg.includes("productcode")
  ) {
    fieldName = "productCode";
  } else if (
    msg.includes("mã vật tư") ||
    msg.includes("material id") ||
    msg.includes("materialid")
  ) {
    fieldName = "materialId";
  } else if (
    msg.includes("mã nhà cung cấp") ||
    msg.includes("vendor code") ||
    msg.includes("vendorid")
  ) {
    fieldName = "code";
  } else if (
    msg.includes("mã thiết bị") ||
    msg.includes("machine id") ||
    msg.includes("machineid")
  ) {
    fieldName = "machineId";
  } else if (
    msg.includes("mã quy trình") ||
    msg.includes("step id") ||
    msg.includes("stepid")
  ) {
    fieldName = "stepId";
  } else if (
    msg.includes("mã số thuế") ||
    msg.includes("tax code") ||
    msg.includes("taxcode")
  ) {
    fieldName = "taxCode";
  } else if (msg.includes("email") || msg.includes("thư điện tử")) {
    fieldName = "email";
  } else if (
    msg.includes("số điện thoại") ||
    msg.includes("phone") ||
    msg.includes("điện thoại")
  ) {
    fieldName = "phone";
  } else if (msg.includes("tên") || msg.includes("name")) {
    fieldName = "name";
  }

  if (fieldName) {
    setTimeout(() => {
      const element =
        document.getElementsByName(fieldName)[0] ||
        document.getElementById(fieldName) ||
        document.querySelector<HTMLElement>(`[name$="${fieldName}"]`);
      if (element && typeof element.focus === "function") {
        element.focus();
        if (
          element instanceof HTMLInputElement ||
          element instanceof HTMLTextAreaElement
        ) {
          element.select();
        }
      }
    }, 50);
  }
}

// 1. SILENT LOADING: Route processing state to Top Header Sync Indicator (0 popup)
toast.loading = (message, data) => {
  const syncMessage = typeof message === "string" ? message : "Đang xử lý...";
  useSyncStore.getState().setSyncing(true, syncMessage);
  return nextSilentId("loading", data?.id);
};

// 2. SILENT DISMISS: Clean up syncing state and only invoke Sonner dismiss for real warning/error toasts
toast.dismiss = (id) => {
  useSyncStore.getState().setSyncing(false);
  if (isSilentToastId(id)) {
    return id ?? "";
  }
  if (id !== undefined) {
    originalDismiss(id);
    return id;
  }
  originalDismiss();
  return "";
};

// 3. SILENT SUCCESS: Never render green floating toast; route label + optional action to Header Sync Pill
toast.success = (message, data) => {
  const text = typeof message === "string" ? message : "Đã lưu thành công";
  const action = extractSyncAction(data);
  useSyncStore.getState().setSuccess(text, { action });
  return nextSilentId("success", data?.id);
};

// 4. SILENT INFO / MESSAGE / CUSTOM: Never render blue/neutral floating toast; route to Header Sync Pill
toast.info = (message, data) => {
  const text = typeof message === "string" ? message : "";
  const action = extractSyncAction(data);
  useSyncStore.getState().setSuccess(text || "Đã cập nhật", { action });
  return nextSilentId("info", data?.id);
};

toast.message = (message, data) => {
  const text = typeof message === "string" ? message : "";
  const action = extractSyncAction(data);
  useSyncStore.getState().setSuccess(text || "Đã cập nhật", { action });
  return nextSilentId("message", data?.id);
};

toast.custom = (_jsx, data) => {
  useSyncStore.getState().setSuccess("Đã cập nhật");
  return nextSilentId("custom", data?.id);
};

// 5. SILENT PROMISE (SUCCESS) + RED TOAST (ERROR):
// Runs caller's success callback side-effects cleanly without showing green toast; surfaces rejection as Red Error Toast.
const silentPromise: typeof toast.promise = (promiseOrFactory, data) => {
  const loadingText =
    typeof data?.loading === "string" ? data.loading : "Đang xử lý...";
  useSyncStore.getState().beginTask(loadingText);

  const rawPromise =
    typeof promiseOrFactory === "function"
      ? promiseOrFactory()
      : promiseOrFactory;

  const tracked = Promise.resolve(rawPromise).then(
    async (result) => {
      let resolvedSuccess = "Đã lưu thành công";
      if (typeof data?.success === "function") {
        const out = await data.success(result);
        if (typeof out === "string" && out.trim()) {
          resolvedSuccess = out;
        }
      } else if (typeof data?.success === "string" && data.success.trim()) {
        resolvedSuccess = data.success;
      }

      useSyncStore.getState().completeTask(true, resolvedSuccess);
      useSyncStore.getState().setSuccess(resolvedSuccess);

      if (typeof data?.finally === "function") {
        await data.finally();
      }
      return result;
    },
    async (err: unknown) => {
      useSyncStore.getState().completeTask(false);
      let resolvedError =
        err instanceof Error && err.message ? err.message : "Đã có lỗi xảy ra";
      if (typeof data?.error === "function") {
        const out = await data.error(err);
        if (typeof out === "string" && out.trim()) {
          resolvedError = out;
        }
      } else if (typeof data?.error === "string" && data.error.trim()) {
        resolvedError = data.error;
      }

      toast.error(resolvedError);

      if (typeof data?.finally === "function") {
        await data.finally();
      }
      throw err;
    },
  );

  return Object.assign(tracked, {
    unwrap: () => tracked,
  }) as ReturnType<typeof toast.promise>;
};

toast.promise = silentPromise;

// 6. EXCEPTION #1 — YELLOW WARNING TOAST (6000ms)
toast.warning = (message, data) => {
  const text = typeof message === "string" ? message : "Cảnh báo";
  useSyncStore.getState().setWarning(text);
  return originalWarning(message, {
    duration: SYNC_WARNING_MS,
    ...data,
  });
};

// 7. EXCEPTION #2 — RED ERROR TOAST (8000ms + Field Auto-Focus)
toast.error = (message, data) => {
  // Máy chủ đang cập nhật: một thông báo duy nhất, không hiện lỗi kỹ thuật của từng trang
  if (isServiceUpdating()) {
    return originalWarning(SERVICE_UPDATING_MESSAGE, { id: SERVICE_UPDATING_TOAST_ID, duration: 8000 });
  }
  const text = typeof message === "string" ? message : "Đã có lỗi xảy ra";
  useSyncStore.getState().setError(text);
  if (typeof message === "string") {
    focusFieldByErrorMessage(message);
  }
  return originalError(message, {
    duration: SYNC_ERROR_MS,
    ...data,
  });
};

// Expose intercepted toast on window for console diagnostics and E2E testing
if (typeof window !== "undefined") {
  (window as unknown as { toast: typeof toast }).toast = toast;
}
